import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useContent } from "../context/ContentContext";
import { Seo } from "../components/Seo";
import { Hero } from "../components/Hero";
import { ContactForm } from "../components/ContactForm";
import { Reveal, Hairline } from "../components/motion";
import { SITE, MEDIA, PHOTOS, REVIEWS, GOOGLE_REVIEWS_URL } from "../lib/site";

const scrollToContact = (e) => {
  e.preventDefault();
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
};

const Marquee = ({ words }) => (
  <div className="relative py-8 border-y border-gold/10 overflow-hidden bg-ink-800/50">
    <div className="flex whitespace-nowrap animate-marquee">
      {[...words, ...words, ...words, ...words].map((w, i) => (
        <span key={`${w}-${i}`} className="font-serif text-2xl md:text-3xl text-cream/80 italic mx-8 flex items-center gap-8">
          {w} <span className="text-gold not-italic text-lg">✦</span>
        </span>
      ))}
    </div>
  </div>
);

const ProfileSection = ({ t, lang }) => (
  <section id="profile" data-testid="profile-section" className="py-16 md:py-24 bg-ink relative overflow-hidden">
    <div className="container-lux relative z-10">
      <div className="max-w-3xl">
        <Reveal><div className="overline mb-8">{t.profile.overline}</div></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl md:text-6xl font-light leading-[1.1] text-cream">{t.profile.heading}</h2>
        </Reveal>
      </div>
      <Hairline className="my-10" delay={0.2} />
      <div className="grid md:grid-cols-2 gap-10 lg:gap-20 max-w-5xl">
        <Reveal delay={0.15}><p className="text-sand text-lg leading-relaxed font-light">{t.profile.p1}</p></Reveal>
        <Reveal delay={0.25}><p className="text-sand text-lg leading-relaxed font-light">{t.profile.p2}</p></Reveal>
      </div>
      <div className="grid sm:grid-cols-3 gap-8 mt-12 border-t border-gold/15 pt-8 max-w-4xl">
        <Reveal delay={0.2}>
          <div className="label-caps mb-2">{t.profile.credentialLabel}</div>
          <div className="font-serif text-2xl text-gold">{SITE.cedula}</div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="label-caps mb-2">{t.profile.locationLabel}</div>
          <div className="text-cream">{SITE.office}</div>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="label-caps mb-2">{lang === "pt" ? "Idiomas" : "Languages"}</div>
          <div className="text-cream">Português · English</div>
        </Reveal>
      </div>
    </div>
    <div aria-hidden="true" className="pointer-events-none absolute -right-8 bottom-0 font-serif text-[16rem] leading-none text-gold/[0.06] select-none hidden lg:block">MJN</div>
  </section>
);

const CARD_CLASS = "group block h-full p-7 md:p-8 bg-ink border border-ink-700 hover:border-gold/60 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-24px_rgba(212,175,55,0.35)] transition-[transform,border-color,box-shadow] duration-500 relative overflow-hidden";

const AreaCard = ({ area, lang, t, path }) => {
  const inner = (
    <>
      <span className="absolute top-0 left-0 h-px w-0 bg-gold group-hover:w-full transition-[width] duration-700 ease-[cubic-bezier(.16,1,.3,1)]" />
      <span className="font-serif text-sm text-gold/70">{area.num}</span>
      <h3 className="font-serif text-2xl text-cream mt-6 mb-3 group-hover:text-gold transition-colors duration-500 leading-snug">{area[lang].title}</h3>
      <p className="text-sand text-sm leading-relaxed font-light">{area[lang].desc}</p>
      <span className="inline-flex items-center gap-2 btn-caps text-gold mt-6 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500">
        {area.route ? t.common.explore : t.common.book} <ArrowUpRight size={14} />
      </span>
    </>
  );
  return area.route ? (
    <Link to={path(area.route)} data-testid={`area-card-${area.id}`} className={CARD_CLASS}>{inner}</Link>
  ) : (
    <a href="#contact" data-testid={`area-card-${area.id}`} className={CARD_CLASS} onClick={scrollToContact}>{inner}</a>
  );
};

const AreasSection = ({ t, lang, path, areas }) => (
  <section id="areas" data-testid="areas-section" className="py-16 md:py-24 bg-ink-800">
    <div className="container-lux">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <Reveal><div className="overline mb-6">{t.areas.overline}</div></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl font-light text-cream max-w-xl">{t.areas.heading}</h2></Reveal>
        </div>
        <Reveal delay={0.2}><p className="text-sand max-w-xs font-light">{t.areas.note}</p></Reveal>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {areas.map((a, i) => (
          <Reveal key={a.id} delay={(i % 3) * 0.1}>
            <AreaCard area={a} lang={lang} t={t} path={path} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WorksSection = ({ t }) => (
  <section id="works" data-testid="works-section" className="py-16 md:py-24 bg-ink">
    <div className="container-lux">
      <Reveal><div className="overline mb-6">{t.works.overline}</div></Reveal>
      <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl font-light text-cream max-w-2xl mb-14">{t.works.heading}</h2></Reveal>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
        {t.works.steps.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.1}>
            <div className="relative">
              <div className="font-serif text-5xl text-gold/25">{s.num}</div>
              <div className="h-px bg-gold/30 my-6" />
              <h3 className="font-serif text-xl text-cream mb-3">{s.title}</h3>
              <p className="text-sand text-sm leading-relaxed font-light">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const GuideTeaser = ({ t, path, guide }) => (
  <section id="guide" data-testid="guide-teaser" className="py-16 md:py-24 bg-ink-700">
    <div className="container-lux grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <Reveal className="order-last lg:order-first">
        <div className="relative max-w-sm mx-auto lg:mx-0">
          <div className="absolute -inset-4 border border-gold/20" />
          <img src={MEDIA.guideCover} alt={guide?.title || "Guide"} loading="lazy" className="relative w-full aspect-square object-cover photo-warm" />
        </div>
      </Reveal>
      <div>
        <Reveal><div className="overline mb-6">{t.guide.overline}</div></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl font-light text-cream">{guide?.title}</h2></Reveal>
        <Hairline className="my-8 max-w-xs" delay={0.2} />
        <Reveal delay={0.2}><p className="text-sand text-lg leading-relaxed font-light max-w-md">{guide?.desc}</p></Reveal>
        <Reveal delay={0.3}>
          <Link to={path("guia")} data-testid="guide-teaser-cta" className="btn-gold btn-caps mt-10 inline-flex">{t.common.viewGuide}</Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const LegacyPhoto = ({ src, caption, testid, className = "", pos = "object-center" }) => (
  <figure className={className}>
    <div className="relative">
      <div className="absolute -inset-3 border border-gold/20" />
      <img src={src} alt={caption} loading="lazy" data-testid={testid} className={`relative w-full aspect-[4/5] object-cover photo-warm ${pos}`} />
    </div>
    <figcaption className="text-sand/70 text-xs mt-5 leading-relaxed">{caption}</figcaption>
  </figure>
);

const LegacySection = ({ t, path }) => (
  <section id="legacy" data-testid="legacy-section" className="py-16 md:py-24 bg-ink-800 overflow-hidden">
    <div className="container-lux grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
      <div className="lg:col-span-6">
        <Reveal><div className="overline mb-6">{t.legacy.overline}</div></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl font-light text-cream leading-tight">{t.legacy.heading}</h2></Reveal>
        <Hairline className="my-8 max-w-xs" delay={0.2} />
        <Reveal delay={0.2}><p className="text-sand text-lg leading-relaxed font-light">{t.legacy.p1}</p></Reveal>
        <Reveal delay={0.3}><p className="text-sand text-lg leading-relaxed font-light mt-6">{t.legacy.p2}</p></Reveal>
        <Reveal delay={0.4}>
          <div className="flex flex-wrap gap-6 mt-10">
            <Link to={path("legado")} data-testid="legacy-page-link" className="btn-gold btn-caps">{t.legacy.more}</Link>
            <Link to={path("historia")} data-testid="story-page-link" className="link-underline btn-caps text-gold self-center">{t.nav.story} →</Link>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-6 grid grid-cols-2 gap-5 items-start">
        <Reveal delay={0.15}>
          <LegacyPhoto src={PHOTOS.father} caption={t.legacy.captionFather} testid="legacy-photo-father" />
        </Reveal>
        <Reveal delay={0.3}>
          <LegacyPhoto src={PHOTOS.posse} pos="object-[50%_25%]" caption={t.legacy.captionFamily} testid="legacy-photo-family" className="mt-10" />
        </Reveal>
      </div>
    </div>
  </section>
);

const ReviewsMarquee = ({ t }) => (
  <section id="reviews" data-testid="reviews-section" className="py-14 md:py-20 bg-ink-800 border-y border-gold/10 overflow-hidden">
    <div className="container-lux flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
      <div>
        <Reveal><div className="overline mb-4">{t.reviews.overline}</div></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-3xl md:text-4xl font-light text-cream">{t.reviews.heading}</h2></Reveal>
      </div>
      <Reveal delay={0.2}>
        <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer" data-testid="reviews-google-link" className="link-underline btn-caps text-gold whitespace-nowrap">
          {t.reviews.rating}
        </a>
      </Reveal>
    </div>
    <div className="relative">
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-10 md:w-28 z-10 bg-gradient-to-r from-ink-800 to-transparent" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-10 md:w-28 z-10 bg-gradient-to-l from-ink-800 to-transparent" />
      <div data-testid="reviews-strip" className="flex w-max animate-marquee-slow hover:[animation-play-state:paused]">
        {[0, 1].map((copy) => (
          <div key={copy} className="flex" aria-hidden={copy === 1 || undefined}>
            {REVIEWS.map((r, i) => (
              <figure key={`${copy}-${r.name}`} data-testid={copy === 0 ? `review-card-${i + 1}` : undefined}
                className="w-[300px] md:w-[400px] shrink-0 mx-3 p-7 md:p-8 bg-ink border border-ink-700">
                <div className="text-gold text-sm tracking-[0.35em]">★★★★★</div>
                <blockquote className="text-sand text-sm leading-relaxed font-light mt-4">“{r.text}”</blockquote>
                <figcaption className="mt-5 font-serif text-cream">{r.name}</figcaption>
                <div className="text-sand/60 text-[10px] mt-1 uppercase tracking-[0.25em]">Google</div>
              </figure>
            ))}
          </div>
        ))}
      </div>
    </div>
    <div className="container-lux mt-8">
      <p className="text-sand/60 text-xs">{t.reviews.note}</p>
    </div>
  </section>
);

const ContactSection = ({ t }) => (
  <section id="contact" data-testid="contact-section" className="py-16 md:py-24 bg-ink">
    <div className="container-lux grid lg:grid-cols-12 gap-12 lg:gap-20">
      <div className="lg:col-span-5">
        <Reveal><div className="overline mb-6">{t.contact.overline}</div></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl font-light text-cream">{t.contact.heading}</h2></Reveal>
        <Reveal delay={0.2}><p className="text-sand text-lg leading-relaxed font-light mt-8 max-w-sm">{t.contact.intro}</p></Reveal>
        <Reveal delay={0.3}>
          <div className="mt-12 space-y-4">
            <div><div className="label-caps mb-1">Email</div><a href={`mailto:${SITE.email}`} className="text-cream link-underline">{SITE.email}</a></div>
            <div><div className="label-caps mb-1">WhatsApp</div><span className="text-cream">{SITE.whatsapp}</span></div>
            <div><div className="label-caps mb-1">{t.profile.locationLabel}</div><span className="text-cream">{SITE.office}</span></div>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal delay={0.15}><ContactForm /></Reveal>
      </div>
    </div>
  </section>
);

export default function Landing() {
  const { lang, t, path } = useLang();
  const { content } = useContent();
  const areas = content?.practiceAreas || [];
  const guide = content?.guide?.[lang];

  return (
    <>
      <Seo
        title={lang === "pt" ? "Maria João Nepomuceno — Advogada de Família · Funchal, Madeira" : "Maria João Nepomuceno — Family Lawyer · Funchal, Madeira"}
        description={lang === "pt" ? "Advocacia de Direito da Família no Funchal, Madeira: divórcio, responsabilidades parentais, pensões de alimentos e apoio às famílias." : "Family law in Funchal, Madeira: divorce, parental responsibilities, child support and dedicated support for families."}
        pageKey="home"
      />
      <Hero />
      <ProfileSection t={t} lang={lang} />
      <LegacySection t={t} path={path} />
      <AreasSection t={t} lang={lang} path={path} areas={areas} />
      <Marquee words={t.marquee} />
      <WorksSection t={t} />
      <GuideTeaser t={t} path={path} guide={guide} />
      <ReviewsMarquee t={t} />
      <ContactSection t={t} />
    </>
  );
}
